import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { IBankStatementEntries } from '../../../domain/usecases/BankStatementEntries'
import { IStandaloneBillings } from '../../../domain/usecases/StandaloneBillings'
import { BankStatementEntry } from '../../../domain/models/BankStatementEntryModel'
import { StandaloneBillingDetailsDrawer } from '../StandaloneBillingsTab/StandaloneBillingDetailsDrawer'
import { usePermissions } from '../../../hooks/usePermissions'

export interface ConciliationTabProps {
  entriesService: IBankStatementEntries
  billingsService: IStandaloneBillings
}

const formatCurrency = (n: number | string | null | undefined) => {
  if (n === null || n === undefined) return '-'
  const v = typeof n === 'string' ? parseFloat(n) : n
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const formatDate = (s: string | null | undefined) => {
  if (!s) return '-'
  const d = new Date(s)
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate()
  ).toLocaleDateString('pt-BR')
}

const sourceLabel = (source: string) => {
  switch (source) {
    case 'itau':
      return 'Itaú'
    case 'manual':
      return 'Manual'
    default:
      return source
  }
}

const monthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

const PAGE_SIZE = 25

export const ConciliationTab: React.FC<ConciliationTabProps> = ({
  entriesService,
  billingsService
}) => {
  const [items, setItems] = useState<BankStatementEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [reconciledFilter, setReconciledFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>(monthStart())
  const [dateTo, setDateTo] = useState<string>('')
  const [detailsBillingId, setDetailsBillingId] = useState<string | null>(null)
  // Sincronizar exige bank_statement_entries.update (o backend recusa com 403). O "Vincular"
  // (lançamento manual → cobrança avulsa) saiu junto com a aba de avulsas em 23/10/2026.
  const { can } = usePermissions()
  const canOperate = can.update('bank_statement_entries')

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, page_size: PAGE_SIZE }
      if (reconciledFilter === 'true') params.reconciled = true
      if (reconciledFilter === 'false') params.reconciled = false
      if (sourceFilter) params.source = sourceFilter
      if (dateFrom) params.entry_date_from = dateFrom
      if (dateTo) params.entry_date_to = dateTo
      const res = await entriesService.list(params)
      setItems(res?.items || [])
      setTotal(res?.total || 0)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [entriesService, page, reconciledFilter, sourceFilter, dateFrom, dateTo])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  useEffect(() => {
    setPage(1)
  }, [reconciledFilter, sourceFilter, dateFrom, dateTo])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handleSync = async () => {
    setSyncing(true)
    try {
      const res: any = await entriesService.sync()
      // O AxiosHttpClient devolve o corpo do erro em vez de lançar
      if (res && res.statusCode && res.statusCode >= 400) {
        alert(res.message || 'Erro ao sincronizar com Itaú')
        return
      }
      alert(
        `Sincronização concluída: ${res.created} novas entradas, ${res.matched} conciliadas`
      )
      await fetchItems()
    } catch (e: any) {
      console.error(e)
      alert(e?.message || 'Erro ao sincronizar com Itaú')
    } finally {
      setSyncing(false)
    }
  }

  const renderMatched = (e: BankStatementEntry) => {
    if (e.matched_billing) {
      const b = e.matched_billing
      const cpp = b.contract_payment_part
      const contract = cpp?.contract
      const unit = contract?.unit
      const project = unit?.building?.project
      return (
        <div>
          <div style={{ fontWeight: 500 }}>
            {cpp?.name || 'Cobrança'}
            {cpp?.installment_number ? ` #${cpp.installment_number}` : ''}
          </div>
          <small className="text-muted d-block">
            {project?.name ? `${project.name} — ` : ''}
            {unit?.name || unit?.number || ''}
          </small>
          <div style={{ marginTop: 4 }}>
            {contract && (
              <Link
                to={`/contracts/${contract.id}`}
                style={{ fontSize: 12, marginRight: 12 }}
              >
                Ver contrato
              </Link>
            )}
            <Link to={`/billings/${b.id}`} style={{ fontSize: 12 }}>
              Ver cobrança
            </Link>
          </div>
        </div>
      )
    }
    if (e.matched_standalone_billing) {
      const m = e.matched_standalone_billing
      const payerName =
        m.payer_person?.info?.name ||
        m.payer_person?.info?.business_name ||
        'Cobrança avulsa'
      return (
        <div>
          <div style={{ fontWeight: 500 }}>{payerName}</div>
          <small className="text-muted">
            Avulsa — {formatCurrency(m.amount)}
          </small>
          <Button
            size="sm"
            variant="link"
            className="p-0 ml-2"
            style={{ fontSize: 12 }}
            onClick={() => setDetailsBillingId(m.id)}
          >
            Ver detalhes
          </Button>
        </div>
      )
    }
    return (
      <Badge variant="warning" style={{ fontSize: 11, padding: '5px 10px' }}>
        Sem conciliação
      </Badge>
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div>
          <h5 style={{ margin: 0, fontWeight: 600 }}>Conciliação do Extrato</h5>
          <small className="text-muted">
            Entradas do extrato bancário Itaú e seu vínculo com as cobranças
          </small>
        </div>
        {canOperate && (
          <Button
            variant="outline-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <Spinner size="sm" animation="border" className="mr-2" />
            ) : (
              <i
                className="feather icon-refresh-cw"
                style={{ marginRight: 6 }}
              />
            )}
            Sincronizar com Itaú
          </Button>
        )}
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="text-muted" style={{ fontSize: 12 }}>
              DE
            </Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="text-muted" style={{ fontSize: 12 }}>
              ATÉ
            </Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="text-muted" style={{ fontSize: 12 }}>
              STATUS
            </Form.Label>
            <Form.Control
              as="select"
              value={reconciledFilter}
              onChange={(e) => setReconciledFilter(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="true">Conciliadas</option>
              <option value="false">Sem conciliação</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="text-muted" style={{ fontSize: 12 }}>
              ORIGEM
            </Form.Label>
            <Form.Control
              as="select"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="itau">Itaú</option>
              <option value="manual">Manual</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th style={{ fontSize: 12, textTransform: 'uppercase' }}>Data</th>
              <th
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  textAlign: 'right'
                }}
              >
                Valor
              </th>
              <th style={{ fontSize: 12, textTransform: 'uppercase' }}>
                Pagador
              </th>
              <th style={{ fontSize: 12, textTransform: 'uppercase' }}>
                Origem
              </th>
              <th style={{ fontSize: 12, textTransform: 'uppercase' }}>
                Cobrança conciliada
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="text-center text-muted py-4"
                  style={{ fontStyle: 'italic' }}
                >
                  Nenhuma entrada encontrada.
                </td>
              </tr>
            )}
            {items.map((e) => {
              return (
                <tr key={e.id}>
                  <td>{formatDate(e.entry_date)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {formatCurrency(e.amount)}
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>{e.payer_name || '-'}</div>
                    {e.payer_document && (
                      <small className="text-muted">{e.payer_document}</small>
                    )}
                  </td>
                  <td>
                    <Badge
                      variant={e.source === 'itau' ? 'info' : 'secondary'}
                      style={{ fontSize: 11, padding: '5px 10px' }}
                    >
                      {sourceLabel(e.source)}
                    </Badge>
                    {e.itau_nosso_numero && (
                      <div>
                        <small className="text-muted">
                          NN: {e.itau_nosso_numero}
                        </small>
                      </div>
                    )}
                  </td>
                  <td>{renderMatched(e)}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {!loading && total > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            flexWrap: 'wrap',
            gap: 8
          }}
        >
          <small className="text-muted">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de{' '}
            {total}
          </small>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="feather icon-chevron-left" />
            </Button>
            <small>
              Página {page} de {totalPages}
            </small>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="feather icon-chevron-right" />
            </Button>
          </div>
        </div>
      )}

      <StandaloneBillingDetailsDrawer
        billingId={detailsBillingId}
        service={billingsService}
        onClose={() => setDetailsBillingId(null)}
      />
    </div>
  )
}
